import { Camera, Shirt, Laptop, Globe, Share2, Download, Check, ArrowRight, Zap, Wifi, TrendingUp, Sparkles } from 'lucide-react';
import { useEffect } from 'react';
import { T, LogoMark, Wordmark } from '../theme';
import { setPageMeta } from '../utils/pageMeta';
import GlobalFooterBar from './GlobalFooterBar';

const PLATFORMS = [
  { name: 'YouTube', color: '#FF0000' },
  { name: 'Instagram', color: '#E1306C' },
  { name: 'TikTok', color: '#010101' },
  { name: 'Twitch', color: '#9146FF' },
  { name: 'Substack', color: '#FF6719' },
  { name: 'Patreon', color: '#FF424D' },
  { name: 'Pinterest', color: '#E60023' },
  { name: 'X / Twitter', color: '#1DA1F2' },
  { name: 'Podcast', color: '#8940FA' },
];

const EXPENSE_TYPES = [
  {
    icon: Camera,
    label: 'Equipment & Gear',
    tip: 'Cameras, lights, mics, tripods, and accessories used for content creation',
  },
  {
    icon: Shirt,
    label: 'Props & Products',
    tip: 'Items purchased for unboxing, reviews, styling shoots, or brand deals',
  },
  {
    icon: Laptop,
    label: 'Software & Subscriptions',
    tip: 'Editing software, stock music, scheduling tools, and analytics platforms',
  },
  {
    icon: Wifi,
    label: 'Studio & Internet',
    tip: 'Home studio, backdrop, lighting setup, and high-speed internet for streaming',
  },
  {
    icon: TrendingUp,
    label: 'Ads & Promotion',
    tip: 'Paid promotion, boosted posts, and sponsored placement to grow your audience',
  },
  {
    icon: Globe,
    label: 'Travel & Events',
    tip: 'Creator conferences, brand trips, location shoots, and content travel',
  },
];

const STEPS = [
  {
    num: '1',
    title: 'Snap the receipt',
    desc: 'Buy a ring light, snap the receipt. Attend a creator event, snap the receipt. AI reads everything instantly — no typing.',
  },
  {
    num: '2',
    title: 'Separate by brand deal',
    desc: 'Create one report per brand partnership. Keep reimbursable expenses cleanly separated from your own business deductions.',
  },
  {
    num: '3',
    title: 'Share or export',
    desc: 'Send a live expense link to a brand for reimbursement, or export a tax-ready report for your accountant at year-end.',
  },
];

const TESTIMONIALS = [
  {
    quote: "I had no idea how much I was spending on gear and software until I started tracking it. Turns out those deductions were cutting my tax bill significantly.",
    name: 'Jordan L.',
    role: 'YouTube creator, 280K subscribers',
  },
  {
    quote: "Brands always ask for expense breakdowns before they reimburse. I just send them a review link and they see exactly what I spent on their campaign.",
    name: 'Sofia M.',
    role: 'Lifestyle & travel influencer',
  },
];

function GetStartedButton({ onSignUp, label = "Get Started — It's Free", size = 'lg' }) {
  const lg = size === 'lg';
  return (
    <button
      onClick={onSignUp}
      style={{
        padding: lg ? '15px 32px' : '12px 24px',
        borderRadius: 14, border: 'none',
        background: T.gradTeal, color: '#fff',
        fontSize: lg ? 16 : 14, fontWeight: 800,
        cursor: 'pointer', boxShadow: '0 6px 24px rgba(5,196,176,0.35)',
        display: 'inline-flex', alignItems: 'center', gap: 8,
      }}
    >
      {label} <ArrowRight size={lg ? 18 : 15} />
    </button>
  );
}

function InfluencerLandingPage({ onSignUp, onSignIn, onSkip, onPricing }) {
  useEffect(() => {
    setPageMeta({
      title: 'Expense Tracking for Content Creators & Influencers',
      description: 'Track gear, props, subscriptions, and brand deal expenses. AI receipt scanning, per-brand reports, and IRS-ready tax exports for creators.',
      url: 'https://myspend.info/?page=influencer',
    });
  }, []);
  return (
    <div style={{ fontFamily: T.fontBody, background: T.appBg, minHeight: '100vh', paddingBottom: 36 }}>

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav style={{
        background: T.navy, padding: '0 24px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 2px 16px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LogoMark size={28} />
          <Wordmark size={18} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={onPricing} style={{
          background: 'none', border: 'none',
          color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600,
          padding: '8px 12px', cursor: 'pointer',
        }}>
          Pricing
        </button>
          <button onClick={onSignIn} style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 10,
            color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
          }}>
            Sign In
          </button>
          <button onClick={onSignUp} style={{
            background: T.gradTeal, border: 'none', borderRadius: 10,
            color: '#fff', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
            boxShadow: '0 3px 12px rgba(5,196,176,0.3)',
          }}>
            Get Started Free
          </button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section style={{ background: T.gradNavy, padding: '72px 24px 80px', textAlign: 'center' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(5,196,176,0.15)', border: '1px solid rgba(5,196,176,0.3)',
            borderRadius: 20, padding: '5px 14px', marginBottom: 24,
          }}>
            <Zap size={13} color={T.tealBright} />
            <span style={{ fontSize: 12, fontWeight: 700, color: T.tealBright, letterSpacing: 0.5 }}>
              AI POWERED FINANCES FOR CONTENT CREATORS & INFLUENCERS
            </span>
          </div>

          <h1 style={{
            fontSize: 'clamp(30px, 6vw, 52px)', fontWeight: 900,
            color: '#fff', margin: '0 0 20px', lineHeight: 1.15, letterSpacing: -1,
          }}>
            Your content is the business.<br />
            <span style={{ color: T.tealBright }}>Treat the expenses that way.</span>
          </h1>

          <p style={{
            fontSize: 18, color: 'rgba(255,255,255,0.65)', margin: '0 0 16px',
            lineHeight: 1.65, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto',
          }}>
            Scan receipts for gear, props, and subscriptions. Separate brand deal expenses
            from personal deductions. Export a tax-ready report in seconds.
          </p>

          <p style={{ fontSize: 15, fontWeight: 800, color: T.tealBright, margin: '0 0 36px', letterSpacing: 0.3 }}>
            Free to use. No credit card. No commitment.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <GetStartedButton onSignUp={onSignUp} label="Get Started Now" />
            <button onClick={onSignIn} style={{
              padding: '15px 32px', borderRadius: 14,
              border: '1.5px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.85)',
              fontSize: 16, fontWeight: 700, cursor: 'pointer',
            }}>
              Sign In
            </button>
          </div>

        </div>
      </section>

      {/* ── Platform strip ───────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '20px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 14 }}>
          For creators on every platform
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', justifyContent: 'center', alignItems: 'center' }}>
          {PLATFORMS.map(p => (
            <span key={p.name} style={{ fontSize: 13, fontWeight: 800, color: p.color, opacity: 0.85 }}>{p.name}</span>
          ))}
        </div>
      </div>

      {/* ── Problem hook ─────────────────────────────────────────────── */}
      <section style={{ padding: '64px 24px', maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
        <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: T.navy, margin: '0 0 16px' }}>
          Most creators overpay taxes by <span style={{ color: T.tealDeep }}>thousands</span> every year
        </h2>
        <p style={{ fontSize: 16, color: '#6B7280', lineHeight: 1.65, maxWidth: 520, margin: '0 0 40px' }}>
          Cameras, software, props, travel — nearly everything you buy to create
          content is tax-deductible. But only if you tracked it.
          Most creators don't, and their accountant can't help what they can't see.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          {['AI receipt scanning', 'Per-brand reports', 'IRS-ready export', 'Free to start'].map(item => (
            <div key={item} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: T.tealPale, borderRadius: 20, padding: '6px 14px',
            }}>
              <Check size={13} color={T.tealDeep} strokeWidth={3} />
              <span style={{ fontSize: 13, fontWeight: 700, color: T.tealDeep }}>{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Expense types ────────────────────────────────────────────── */}
      <section style={{ background: '#fff', padding: '64px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 8px' }}>
            Every creator expense, tracked
          </h2>
          <p style={{ fontSize: 15, color: '#9CA3AF', textAlign: 'center', marginBottom: 40 }}>
            Categories map to IRS Schedule C — so deductions are correct at tax time.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {EXPENSE_TYPES.map(({ icon: Icon, label, tip }) => (
              <div key={label} style={{
                display: 'flex', alignItems: 'flex-start', gap: 14,
                background: T.appBg, borderRadius: 14, padding: '18px 20px',
                border: '1.5px solid #E5E7EB',
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                  background: T.tealPale, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={20} color={T.tealDeep} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: T.navy, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>{tip}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────── */}
      <section style={{ padding: '64px 24px', maxWidth: 700, margin: '0 auto' }}>
        <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 48px' }}>
          From purchase to tax deduction in three steps
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {STEPS.map((step, i) => (
            <div key={i} style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
              <div style={{
                width: 44, height: 44, borderRadius: 22, flexShrink: 0,
                background: T.gradTeal, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 900, color: '#fff',
                boxShadow: '0 4px 14px rgba(5,196,176,0.35)',
              }}>
                {step.num}
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 800, color: T.navy, marginBottom: 6 }}>{step.title}</div>
                <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6 }}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Brand deal sharing callout ───────────────────────────────── */}
      <section style={{ background: T.gradNavy, padding: '64px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, background: 'rgba(5,196,176,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px', border: '1px solid rgba(5,196,176,0.3)',
          }}>
            <Share2 size={30} color={T.tealBright} />
          </div>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: '#fff', margin: '0 0 16px' }}>
            Get reimbursed by brands faster
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, marginBottom: 16 }}>
            Create a separate report for each brand partnership. Generate a secure
            review link and send it to the brand's campaign manager — they see a clean,
            read-only expense breakdown with no account required.
          </p>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.45)', marginBottom: 32 }}>
            Comments and approvals happen right on the report. No email chains.
          </p>
          <GetStartedButton onSignUp={onSignUp} label="Get Started Now" />
        </div>
      </section>

      {/* ── AI Bookkeeper callout ───────────────────────────────────── */}
      <section style={{ background: T.tealPale, padding: '64px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 20, background: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
          }}>
            <Sparkles size={30} color={T.tealDeep} />
          </div>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: 900, color: T.navy, margin: '0 0 16px' }}>
            Know your numbers without the spreadsheet
          </h2>
          <p style={{ fontSize: 16, color: '#4B5563', lineHeight: 1.65, marginBottom: 28 }}>
            Ask the AI Bookkeeper questions about your creator business — from total gear spend to per-brand deal breakdowns. Plain English, instant answers.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, textAlign: 'left', marginBottom: 32 }}>
            {[
              '"How much did I spend on equipment this year?"',
              '"Chart my spending by category for Q1"',
              '"What\'s my total for brand deal expenses in Q3?"',
              '"Which month had my highest content expenses?"',
            ].map(q => (
              <div key={q} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <Check size={11} color={T.tealDeep} />
                </div>
                <span style={{ fontSize: 14, color: '#374151', fontStyle: 'italic' }}>{q}</span>
              </div>
            ))}
          </div>
          <GetStartedButton onSignUp={onSignUp} label="Try It Free" />
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────────── */}
      <section style={{ background: '#fff', padding: '64px 24px' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: 900, color: T.navy, textAlign: 'center', margin: '0 0 40px' }}>
            What creators are saying
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} style={{
                background: T.appBg, borderRadius: 16, padding: '24px',
                border: '1.5px solid #E5E7EB',
              }}>
                <div style={{ fontSize: 28, color: T.tealBright, fontWeight: 900, lineHeight: 1, marginBottom: 12 }}>"</div>
                <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.65, margin: '0 0 16px', fontStyle: 'italic' }}>
                  {t.quote}
                </p>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{t.name}</div>
                <div style={{ fontSize: 12, color: '#9CA3AF' }}>{t.role}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', textAlign: 'center', background: T.gradNavy }}>
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 900, color: '#fff', margin: '0 0 16px', lineHeight: 1.2 }}>
            Create the content.<br />
            <span style={{ color: T.tealBright }}>Keep the deductions.</span>
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', margin: '0 0 32px', lineHeight: 1.6 }}>
            Free forever for basic tracking. No credit card required.
          </p>
          <GetStartedButton onSignUp={onSignUp} label="Get Started Now — It's Free" />
        </div>
      </section>

      <GlobalFooterBar />

    </div>
  );
}

export default InfluencerLandingPage;
